"use client";

import React from "react";
import { m as motion, AnimatePresence } from "framer-motion";

const PhoneFrame = ({
  children,
  className,
  style,
}: {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const baseClasses =
    "relative w-[100px] md:w-[160px] h-[200px] md:h-[320px] rounded-[1rem] md:rounded-[2rem] border-[3px] md:border-[6px] bg-zinc-950 border-zinc-900 dark:bg-zinc-200 dark:border-zinc-600 overflow-hidden shadow-xl";
  const finalClassName = className
    ? `${baseClasses} ${className}`
    : baseClasses;

  return (
    <div className={finalClassName} style={style}>
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 md:w-16 h-2 md:h-4 bg-zinc-900 rounded-b-md md:rounded-b-lg z-20" />
      {/* Screen Content */}
      <div className="w-full h-full bg-background relative z-10 overflow-hidden">
        {children}
      </div>
    </div>
  );
};

const MockScreenContent = ({
  type = "list",
  title,
  imageUrl,
}: {
  type?: "list" | "detail" | "profile" | "image";
  title?: string;
  imageUrl?: string;
}) => {
  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-800 overflow-hidden">
      {/* If image type, show full image */}
      {type === "image" && imageUrl ? (
        <div className="w-full h-full relative">
          <AnimatePresence mode="popLayout">
            <motion.img
              key={imageUrl}
              src={imageUrl}
              alt={title || "App Screenshot"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover object-top select-none pointer-events-none"
            />
          </AnimatePresence>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="pt-6 pb-2 px-2 border-b border-zinc-200 dark:border-gray-500 bg-zinc-50 dark:bg-gray-600 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-400 flex items-center justify-center">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500 dark:bg-blue-200" />
              </div>
              <div className="text-[7px] font-bold uppercase tracking-widest text-gray-600 dark:text-white">
                {title || "App"}
              </div>
              <div className="w-5 h-5 rounded-full bg-gray-300 dark:bg-gray-400" />
            </div>
          </div>

          {/* Content */}
          <div className="px-2 py-2 space-y-2 overflow-y-auto overflow-x-hidden">
            {type === "list" &&
              [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-12 rounded-lg bg-gray-200 dark:bg-gray-500 border border-gray-300 dark:border-gray-400 p-1.5 flex items-center gap-1.5 shadow-sm"
                >
                  <div className="w-8 h-8 rounded-md bg-blue-200 dark:bg-blue-400 flex-shrink-0" />
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="w-3/4 h-1.5 rounded-full bg-gray-400 dark:bg-gray-400" />
                    <div className="w-1/2 h-1 rounded-full bg-gray-300 dark:bg-gray-400" />
                  </div>
                </div>
              ))}

            {type === "detail" && (
              <div className="space-y-2 p-2">
                <div className="w-full aspect-video rounded-lg bg-blue-200 dark:bg-blue-400 mb-1.5" />
                <div className="space-y-1">
                  <div className="w-full h-1.5 rounded-full bg-gray-400 dark:bg-gray-400" />
                  <div className="w-full h-1.5 rounded-full bg-gray-400 dark:bg-gray-400" />
                  <div className="w-2/3 h-1.5 rounded-full bg-gray-400 dark:bg-gray-400" />
                </div>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  <div className="h-14 rounded-lg bg-gray-200 dark:bg-gray-500" />
                  <div className="h-14 rounded-lg bg-gray-200 dark:bg-gray-500" />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface PhoneCardProps {
  type?: "image" | "list" | "detail";
  imageUrl?: string;
  title?: string;
  position?: "left" | "center" | "right";
  blur?: boolean;
  opacity?: number;
}

export const PhoneCard = ({
  type = "list",
  imageUrl,
  title = "App",
  position = "center",
  blur = false,
  opacity = 1,
}: PhoneCardProps) => {
  const isCenter = position === "center";
  const isLeft = position === "left";

  if (isCenter) {
    return (
      <motion.div className="relative z-20" whileHover={{ scale: 1.08, y: -8 }}>
        <PhoneFrame className="shadow-[0_15px_40px_-10px_rgba(0,0,0,0.5)] border-zinc-900 !w-[160px] !h-[320px]">
          <MockScreenContent type={type} title={title} imageUrl={imageUrl} />
        </PhoneFrame>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={`absolute ${
        isLeft ? "left-0" : "right-0"
      } top-1/2 -translate-y-1/2 z-10 origin-${
        isLeft ? "bottom-right" : "bottom-left"
      } hidden sm:block ${blur ? `blur-[${blur ? "2px" : "0px"}]` : ""}`}
      style={{
        opacity,
        ...(blur && { filter: `blur(${blur ? "2px" : "0px"})` }),
      }}
      whileHover={
        !blur
          ? {
              scale: 1.05,
              rotate: isLeft ? -12 : 12,
              x: isLeft ? -15 : 15,
              zIndex: 30,
            }
          : {}
      }
      initial={{ rotate: isLeft ? -6 : 6, x: isLeft ? 15 : -15, scale: 0.75 }}
    >
      <PhoneFrame className="shadow-xl border-zinc-800/50 !w-[140px] !h-[280px]">
        <MockScreenContent type={type} title={title} imageUrl={imageUrl} />
      </PhoneFrame>
    </motion.div>
  );
};

const LaptopFrame = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={`relative flex flex-col items-center ${className}`}>
      {/* Screen Area */}
      <div className="relative w-full aspect-[16/10] bg-zinc-950 rounded-[1.2rem] md:rounded-[1.8rem] border-[5px] md:border-[8px] border-zinc-900 dark:border-zinc-800 overflow-hidden shadow-2xl z-20">
        <div className="absolute inset-0 bg-background overflow-hidden flex items-center justify-center">
          {children}
        </div>
        {/* Subtle Screen Reflection */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/5 to-transparent opacity-30" />
        {/* Camera */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-zinc-800/80 shadow-inner" />
      </div>
      
      {/* Stand Neck */}
      <div className="relative w-[18%] h-6 md:h-10 bg-gradient-to-b from-zinc-800 to-zinc-900 dark:from-zinc-700 dark:to-zinc-800 -mt-2 z-10 shadow-lg" />
      
      {/* Stand Base */}
      <div className="relative w-[40%] h-1.5 md:h-2.5 bg-gradient-to-r from-zinc-700 via-zinc-800 to-zinc-700 dark:from-zinc-600 dark:via-zinc-700 dark:to-zinc-600 rounded-t-lg shadow-2xl" />
      
      {/* Global Shadow under stand */}
      <div className="w-[35%] h-2 bg-black/40 blur-md rounded-full -mt-1 opacity-60" />
    </div>
  );
};

export { PhoneFrame, MockScreenContent, LaptopFrame };
